import { action, computed, observable } from 'mobx';
import TransactionRepository from '../../repositories/TransactionRepository/TransactionRepository';
import ConfigRepository from '../../repositories/ConfigRepository';
import { PaymentRepository } from '../../repositories/PaymentRepository/PaymentRepository';
import GiftCardUIState from '../../helpers/UIState/GiftCardUIState';
import PaymentService from '../../application/services/PaymentService';
import Transaction from '../../domain/entities/Transaction/Transaction';
import PAYMENT_PROVIDERS, { PAYMENT_MODES } from '../../constants/PaymentProviders';
import TranslationRepository from '../../repositories/TranslationRepository';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import { GIFT_CARD } from '../../constants/Errors';
import formatPrice from '../../utils/PriceFormatUtils';
import { GiftCard } from '../../api/IDataMappings';
import Config from '../../domain/entities/Config';
import TranslationUtils from '../../utils/TranslationUtils';
import { NOTIFICATION_TYPE } from '../../constants/Notifications';
import { GiftCardInput } from '../../api';
import { GiftCardLimits } from '../../constants/Limits';

export interface IGiftCardViewControllerData {
  giftCardErrorMessage: string;
  giftCards: GiftCard[];
  giftCardSuccessMessage: string;
  hasARProducts: boolean;
  hasGiftCardsApplied: boolean;
  maxGiftCardNumberLength: number;
  maxGiftCards: number;
  maxPinNumberLength: number;
  moreDetailsGiftCardLabel: string;
  shouldDisplay: boolean;
  shouldDisplayForm: boolean;
  totalCost: number;
}

export interface IGiftCardViewControllerConfig {
  configRepository: ConfigRepository;
  notificationsCollection: NotificationsCollection;
  paymentRepository: PaymentRepository;
  paymentService: PaymentService;
  transactionRepository: TransactionRepository;
  translationRepository: TranslationRepository;
  giftCardUIState: GiftCardUIState;
}

export default class GiftCardViewController {

  private transactionRepository: TransactionRepository;

  private configRepository: ConfigRepository;

  private paymentRepository: PaymentRepository;

  private translationRepository: TranslationRepository;

  private paymentService: PaymentService;

  private giftCardUIState: GiftCardUIState;

  @observable notificationsCollection: NotificationsCollection;

  constructor(config: IGiftCardViewControllerConfig) {
    this.transactionRepository = config.transactionRepository;
    this.configRepository = config.configRepository;
    this.paymentService = config.paymentService;
    this.giftCardUIState = config.giftCardUIState;
    this.paymentRepository = config.paymentRepository;
    this.translationRepository = config.translationRepository;
    this.notificationsCollection = config.notificationsCollection;
  }

  @action loaded(): void {
    this.giftCardUIState.resetGiftCardMessages();
  }

  @computed
  private get config(): Config {
    return this.configRepository.config;
  }

  @computed
  private get transaction(): Transaction {
    return this.transactionRepository.transaction;
  }

  @computed
  private get selectedPaymentMethod(): PAYMENT_PROVIDERS {
    return this.paymentService?.paymentProvider;
  }

  @computed
  private get selectedPaymentMode(): PAYMENT_MODES {
    return this.paymentService?.paymentMode;
  }

  @computed
  private get hasGiftCardsApplied(): boolean {
    return this.transaction?.payment.areGiftCardsApplied;
  }

  @computed
  private get maxGiftCards(): number {
    return this.transaction?.payment.maxGiftCards;
  }

  @computed
  private get maxGiftCardNumberLength(): number {
    return this.config?.maxGiftCardNumberLength || GiftCardLimits.maxGiftCardNumberLength;
  }

  @computed
  private get maxPinNumberLength(): number {
    return GiftCardLimits.maxPinNumberLength;
  }

  @computed
  private get isGiftCardUnavailable(): boolean {
    return this.transaction && this.notificationsCollection.hasNotification(GIFT_CARD.UNAVAILABLE);
  }

  @computed
  private get isGiftCardBalanceZero(): boolean {
    return this.transaction && this.notificationsCollection.isGiftCardBalanceZero;
  }

  @computed
  private get shouldDisplay(): boolean {

    return !!this.maxGiftCards;
  }

  @computed
  private get shouldDisplayForm(): boolean {
    const { payment } = this.transaction;

    return this.shouldDisplay && (
      payment.isGiftCardPaymentSupportedByMethodAndMode(
        this.selectedPaymentMethod,
        this.selectedPaymentMode,
      ) || this.hasGiftCardsApplied
    ) && !this.hasARProducts;
  }

  @computed
  private get giftCardErrorMessage(): string {
    const { translationSchema  } = this.translationRepository;

    if (this.isGiftCardUnavailable) {
      return translationSchema.giftCardUnavailable;
    }

    if (this.isGiftCardBalanceZero) {
      return translationSchema.giftCardZeroBalance;
    }

    if (this.giftCardUIState.isGCErrorMessageVisible) {
      return translationSchema.giftCardError;
    }

    if (this.giftCardUIState.isGCRemovedMessageVisible) {
      return translationSchema.giftCardRemoved;
    }

    return null;
  }

  @computed
  private get giftCardSuccessMessage(): string {
    const {
      translationSchema: {
        giftCardApplied,
      },
    } = this.translationRepository;

    const lastGiftCardApplied = this.giftCards?.[this.giftCards.length - 1];
    const successMessage = this.giftCards.length
      ? TranslationUtils
        .replace(giftCardApplied, '::applied_credit::', lastGiftCardApplied.amount)
        .replace('::balance::', lastGiftCardApplied.balance)
        .replace('::order_total::', this.getPrice(this.totalCost))
      : null;

    return this.giftCardUIState.isGCSuccessMessageVisible ? successMessage : null;
  }

  @computed
  private get moreDetailsGiftCardLabel(): string {
    const {
      translationSchema: {
        giftCardMoreInfo,
      },
    } = this.translationRepository;
    return this.paymentRepository.isOrderFullyCoveredByGiftCard ? 
      '' : giftCardMoreInfo;
  }

  @computed
  private get totalCost(): number {
    return this.transaction?.orderSummary.totalCost;
  }

  @computed
  private get giftCards(): GiftCard[] {
    return this.transaction?.payment?.giftCards.map(gc => ({
      ...gc,
      amount: this.getPrice(gc.amount as number),
      balance: this.getPrice(gc.balance as number),
    })) || [];
  }

  @computed
  private get hasARProducts(): boolean {
    return this.transaction?.cart.cartItems.some(item => item.isAutoReplenish);
  }

  @computed
  get data(): IGiftCardViewControllerData {
    return {
      giftCardErrorMessage: this.giftCardErrorMessage,
      giftCardSuccessMessage: this.giftCardSuccessMessage,
      giftCards: this.giftCards,
      hasARProducts: this.hasARProducts,
      hasGiftCardsApplied: this.hasGiftCardsApplied,
      maxGiftCardNumberLength: this.maxGiftCardNumberLength,
      maxGiftCards: this.maxGiftCards,
      maxPinNumberLength: this.maxPinNumberLength,
      moreDetailsGiftCardLabel: this.moreDetailsGiftCardLabel,
      shouldDisplay: this.shouldDisplay,
      shouldDisplayForm: this.shouldDisplayForm,
      totalCost: this.totalCost,
    };
  }

  private getPrice = (price: number): string => this.config && formatPrice(this.config, price);

  onAddGiftCard = async (giftCard: GiftCardInput): Promise<void> => {
    this.giftCardUIState.resetGiftCardMessages();
    this.notificationsCollection.clearNotifications(undefined, NOTIFICATION_TYPE.DANGER);

    try {
      await this.paymentService.paymentStrategy.addGiftCard(giftCard);
      this.giftCardUIState.showGiftCardSuccessMessage();
      this.paymentRepository.setupSelectedPayment();
    } catch (err) {
      this.giftCardUIState.showGiftCardErrorMessage();
      throw err;
    }
  };

  onRemoveGiftCard = async (giftCardId: string): Promise<void> => {
    this.giftCardUIState.resetGiftCardMessages();

    try {
      await this.paymentService.paymentStrategy.removeGiftCard(giftCardId);
      this.giftCardUIState.showGiftCardRemovedMessage();
      this.paymentRepository.setupSelectedPayment();
    } catch (err) {
      this.giftCardUIState.showGiftCardErrorMessage();
      throw err;
    }
  };
}